import { isEmpty } from 'lodash';
import React from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { MdClose, MdLibraryBooks } from 'react-icons/md';
import { titleOpinionApi } from '~/api';
import { Modal } from '~/components';
import { FormGroup, TextArea } from '~/components/form';
import { useForm, useLoadSection, useAutoSave, useRouter, useAsync, asyncState } from '~/hooks';
import { stringUtils } from '~/util';
import { prefillFromPTO, prefillFromSearch } from '../../prefill';
const { hasValue } = stringUtils;
import sectionTypes from '../sectionTypes';
import DeedInfo from './DeedInfo';


const defaultState = {
    fileNumber: '',
    commitmentNumber: '',
    b2Exceptions: '',
    sectionDetails: [],
    fileNumberPrompt: false,
    fileNumberData: null
};

const File = ({ titleOpinionId, readonly, hasFileNumber }) => {
        
    const { onLoad } = useLoadSection(sectionTypes.FILE, titleOpinionId);
    const { saveField } = useAutoSave(sectionTypes.FILE, titleOpinionId);

    const {
        state,
        fieldActions
    } = useForm({
        initialState: defaultState,
        onLoad 
    });

    // const saveOnBlur = (value, path, validator) => {
    //     saveField(value, path, state.values.sectionId);
    //     return fieldActions.onBlur(value, path, validator);
    // };

    const saveOnChange = (value, path) => {
        saveField(value, path, state.values.sectionId);
        return fieldActions.onChange(value, path);
    };

    const handleFileNumber = async (value) => {
        if (hasValue(value)) {

            const maybeConflicts = await titleOpinionApi.verifyFileNumber(value);
            
            if (Array.isArray(maybeConflicts)) {
                fieldActions.onChange(maybeConflicts, 'fileNumberData');
                fieldActions.onChange(true, 'fileNumberPrompt');
            } else {
                saveField(value, 'fileNumber', state.values.sectionId);
            }
        } else {
            saveField(value, 'fileNumber', state.values.sectionId);
        }
    };

    const _readonly = readonly || !hasFileNumber;
    
    return (
        <div className="mx-5">
            <div className="mb-5">
                <div>
                    <div className="z-10 flex items-center h-full text-xl font-semibold text-blue-60">
                        <div className="h-11 w-11 rounded-md flex items-center justify-center bg-blue-30"><MdLibraryBooks size="45%"/></div>
                        <h2 className="ml-2"> File &amp; Deed Information </h2>
                    </div> 
                </div>
            </div>
            <span className="block text-sm text-neutral-90 mb-2">Please note any information changes will overwrite any previously entered. Changes are saved automatically.</span>
            <div className='grid grid-cols-12 grid-flow-row gap-8 text-sm my-4 mb-8'>
                <p className='text-base font-medium mt-4 -mb-4 col-span-12'>File Information</p>
                <FormGroup
                    label='Your File No.'
                    required
                    readOnly={readonly}
                    groupClassName="col-span-6"
                    inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                    path='fileNumber' 
                    value={state.values.fileNumber}
                    error={state.errors.fileNumber}
                    {...fieldActions}
                    onBlur={handleFileNumber}
                />
                <FormGroup
                    label='Supplementing Commitment No.'
                    required
                    groupClassName="col-span-6"
                    inputClassName="w-full border-1 border-gray-200 rounded bg-gray-50"
                    path='commitmentNumber'
                    readOnly={_readonly} 
                    value={state.values.commitmentNumber}
                    error={state.errors.commitmentNumber}
                    {...fieldActions}
                    onChange={saveOnChange}
                />
                <TextArea 
                    type='textarea'
                    readOnly={_readonly}
                    label='Supplementing the Preliminary Opinion on Title, the undersigned certifies all Requirements in Schedule B-I are satisfied except:'
                    groupClassName="col-span-12"
                    inputClassName="mt-2 w-full border-1 border-gray-200 rounded bg-gray-50"
                    path='b2Exceptions'
                    value={state.values.b2Exceptions}
                    error={state.errors.b2Exceptions}
                    {...fieldActions}
                    onChange={saveOnChange}
                />
                <DeedInfo titleOpinionId={titleOpinionId} readonly={readonly} hasFileNumber={hasFileNumber}/>
                <FileNumberPrompt 
                    isOpen={state.values.fileNumberPrompt} 
                    data={state.values.fileNumberData}
                    closeModal={() => fieldActions.onChange(false, 'fileNumberPrompt')}
                    clearValue={() => fieldActions.onChange('', 'fileNumber')}
                />
            </div>
        </div>
    );
};

export default File;

function FileNumberPrompt ({ isOpen, closeModal, clearValue, data = []}) {
    const { replace, pathname } = useRouter();
    
    const hasData =  Array.isArray(data) && !isEmpty(data[0]);
    const multiple = hasData && data.length > 1;

    
    const psrMatch = hasData && /^psr:/i.test(data[0].status);
    const ptoMatch = hasData && /^pto:/i.test(data[0].status);
    
    const handlePrefill = async () => {
        if (ptoMatch) {
            await prefillFromPTO(data[0].id)
                .then(() => replace(pathname.replace('/0/', `/${data[0].id}/`)));
        } else if (psrMatch) {
            await prefillFromSearch(data[0].id, data[0].fileNumber)
                .then((id) => replace(pathname.replace('/0/', `/${id}/`)));
        }
    };
    
    const prefill = useAsync(handlePrefill, false);

    let prompt;
    if (ptoMatch) {
        prompt = `We found a preliminary title opinion for  the file number ${data[0].fileNumber}`;
        prompt = `${prompt} at ${data[0].propertyAddress ? data[0].propertyAddress : data[0].streetAddress}`;
    } else if (psrMatch) {
        prompt = `We found a prior policy search record for file number ${data[0].fileNumber} at ${data[0].streetAddress}`;
    }

    return (
        <Modal isModalOpen={isOpen} className='w-1/2 my-auto focus:outline-none'>
            {prefill.status == asyncState.PENDING && (
                <AiOutlineLoading3Quarters className='text-blue-30 animate-spin m-auto my-24' size='6rem' />
            )}
            {prefill.status == asyncState.IDLE && (
                <section className='h-full bg-white rounded-md flex flex-col focus:outline-none'>
                    <header className='pt-3 pb-2 px-8 border-b flex items-center justify-between'>
                        <p className='font-semibold'>Existing File Found</p>
                        <button 
                            className='rounded-full p-2 hover:bg-gray-150 focus:outline-none'
                            onClick={() => {
                                clearValue();
                                closeModal();
                            }}
                        >
                            <MdClose />
                        </button>
                    </header>
                    { multiple && (
                        <div className='bg-error-10 text-error-60 py-2 px-8'>
                            <p>Found multiple {ptoMatch ? 'title opinions' : 'policy search requests'} with the same file number</p>
                        </div>
                    )}
                    <div className='px-8 py-4'>
                        <p>{prompt}.</p>
                    </div>
                    <p className='italic m-4 text-center'>Is this the file you are submitting a final opinion for?</p>
                    <div className='flex items-center mb-4'>
                        <button 
                            className='bg-primary-60 hover:bg-primary-50 text-white rounded-full py-2 px-4 focus:outline-none font-medium ml-auto'
                            onClick={prefill.execute}
                        >
                            Yes
                        </button>
                        <button
                            className='bg-gray-200 hover:bg-gray-400 font-medium rounded-full py-2 px-4 focus:outline-none ml-4 mr-auto'
                            onClick={() => {
                                clearValue();
                                closeModal();
                            }}
                        >
                            No
                        </button>
                    </div>
                </section>
            )}
        </Modal>
    );
}